if (!customElements.get('media-gallery')) {
  customElements.define(
    'media-gallery',
    class MediaGallery extends HTMLElement {
      constructor() {
        super();
        this.elements = {
          liveRegion: this.querySelector('[id^="GalleryStatus"]'),
          viewer: this.querySelector('[id^="GalleryViewer"]'),
          thumbnails: this.querySelector('[id^="GalleryThumbnails"]'),
          controls: this.querySelector('[data-rp-gallery-controls]'),
          previous: this.querySelector('[data-rp-gallery-prev]'),
          next: this.querySelector('[data-rp-gallery-next]'),
          current: this.querySelector('[data-rp-gallery-current]'),
          total: this.querySelector('[data-rp-gallery-total]'),
        };
        this.mql = window.matchMedia('(min-width: 750px)');
        this.syncMediaVisibility();
        this.initImageStates();
        this.bindGalleryControls();
        this.updateGalleryControls();
        if (!this.elements.thumbnails) return;

        this.elements.viewer.addEventListener('slideChanged', debounce(this.onSlideChanged.bind(this), 500));
        this.elements.thumbnails.querySelectorAll('[data-target]').forEach((mediaToSwitch) => {
          mediaToSwitch
            .querySelector('button')
            .addEventListener('click', this.setActiveMedia.bind(this, mediaToSwitch.dataset.target, false));
        });
        if (this.dataset.desktopLayout.includes('thumbnail') && this.mql.matches) this.removeListSemantic();
      }

      bindGalleryControls() {
        this.elements.previous?.addEventListener('click', () => this.navigateMedia(-1));
        this.elements.next?.addEventListener('click', () => this.navigateMedia(1));
      }

      getVisibleThumbnailTargets() {
        if (!this.elements.thumbnails) return [];

        return Array.from(this.elements.thumbnails.querySelectorAll('[data-target]')).filter(
          (thumbnail) =>
            !thumbnail.hidden &&
            !thumbnail.matches('.thumbnail-list_item--variant:not(:first-child)')
        );
      }

      syncMediaVisibility() {
        const mediaItems = Array.from(this.elements.viewer?.querySelectorAll('[data-media-id]') || []);
        const visibleThumbnailTargets = this.getVisibleThumbnailTargets();
        if (!mediaItems.length || !visibleThumbnailTargets.length) return mediaItems;

        const visibleMediaIds = new Set(visibleThumbnailTargets.map((thumbnail) => thumbnail.dataset.target));
        mediaItems.forEach((mediaItem) => {
          const isFiltered = !visibleMediaIds.has(mediaItem.dataset.mediaId);
          mediaItem.classList.toggle('rp-gallery-media--filtered', isFiltered);
          if (isFiltered) {
            mediaItem.classList.remove('is-active');
            mediaItem.setAttribute('aria-hidden', 'true');
          } else {
            mediaItem.removeAttribute('aria-hidden');
          }
        });

        const visibleMediaItems = mediaItems.filter(
          (mediaItem) => !mediaItem.classList.contains('rp-gallery-media--filtered')
        );
        if (visibleMediaItems.length && !visibleMediaItems.some((mediaItem) => mediaItem.classList.contains('is-active'))) {
          visibleMediaItems[0].classList.add('is-active');
          this.elements.thumbnails.querySelectorAll('button').forEach((button) => button.removeAttribute('aria-current'));
          const firstThumbnail = visibleThumbnailTargets.find(
            (thumbnail) => thumbnail.dataset.target === visibleMediaItems[0].dataset.mediaId
          );
          firstThumbnail?.querySelector('button')?.setAttribute('aria-current', 'true');
        }

        return visibleMediaItems;
      }

      getNavigableMedia() {
        const synchronizedMedia = this.syncMediaVisibility();
        return synchronizedMedia.filter((mediaItem) => !mediaItem.classList.contains('rp-gallery-media--filtered'));
      }

      getActiveMediaIndex(mediaItems = this.getNavigableMedia()) {
        const activeIndex = mediaItems.findIndex((item) => item.classList.contains('is-active'));
        return activeIndex >= 0 ? activeIndex : 0;
      }

      navigateMedia(direction) {
        const mediaItems = this.getNavigableMedia();
        if (mediaItems.length < 2) return;
        const targetIndex = (this.getActiveMediaIndex(mediaItems) + direction + mediaItems.length) % mediaItems.length;
        this.setActiveMedia(mediaItems[targetIndex].dataset.mediaId, false);
      }

      updateGalleryControls(activeMedia) {
        const mediaItems = this.getNavigableMedia();
        if (this.elements.controls) this.elements.controls.hidden = mediaItems.length < 2;
        if (this.elements.total) this.elements.total.textContent = mediaItems.length;
        if (!mediaItems.length || !this.elements.current) return;
        const currentIndex = activeMedia ? mediaItems.indexOf(activeMedia) : this.getActiveMediaIndex(mediaItems);
        this.elements.current.textContent = Math.max(0, currentIndex) + 1;
      }

      initImageStates() {
        this.querySelectorAll('.product__modal-opener--image').forEach((opener) => {
          const image = opener.querySelector('.product__media img');
          const spinner = opener.querySelector(':scope > .loading__spinner');
          const errorMessage = opener.querySelector('.product__media-error');
          if (!image) return;

          const finish = (failed) => {
            spinner?.classList.add('hidden');
            if (spinner) spinner.hidden = true;
            if (errorMessage) errorMessage.hidden = !failed;
            opener.classList.toggle('product__modal-opener--error', failed);
          };

          image.addEventListener('load', () => finish(false));
          image.addEventListener('error', () => finish(true));
          if (image.complete) finish(image.naturalWidth === 0);
        });
      }
      onSlideChanged(event) {
        const activeMedia = event.detail?.currentElement;
        if (!activeMedia) return;
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.toggle('is-active', element === activeMedia);
        });
        this.updateGalleryControls(activeMedia);
        if (!this.elements.thumbnails) return;
        const thumbnail = this.elements.thumbnails.querySelector(
          `[data-target="${activeMedia.dataset.mediaId}"]`
        );
        this.setActiveThumbnail(thumbnail);
      }

      setActiveMedia(mediaId, prepend) {
        const activeMedia =
          this.elements.viewer.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.elements.viewer.querySelector('[data-media-id]');
        if (!activeMedia) {
          return;
        }
        this.elements.viewer.querySelectorAll('[data-media-id]').forEach((element) => {
          element.classList.remove('is-active');
        });
        activeMedia?.classList?.add('is-active');

        if (prepend) {
          activeMedia.parentElement.firstChild !== activeMedia && activeMedia.parentElement.prepend(activeMedia);

          if (this.elements.thumbnails) {
            const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
            activeThumbnail.parentElement.firstChild !== activeThumbnail && activeThumbnail.parentElement.prepend(activeThumbnail);
          }

          if (this.elements.viewer.slider) this.elements.viewer.resetPages();
        }

        this.syncMediaVisibility();
        this.updateGalleryControls(activeMedia);

        this.preventStickyHeader();
        window.setTimeout(() => {
          if (!this.mql.matches || this.elements.thumbnails) {
            activeMedia.parentElement.scrollTo({ left: activeMedia.offsetLeft });
          }
          const activeMediaRect = activeMedia.getBoundingClientRect();
          // Don't scroll if the image is already in view
          if (activeMediaRect.top > -0.5) return;
          const top = activeMediaRect.top + window.scrollY;
          window.scrollTo({ top: top, behavior: 'smooth' });
        });
        this.playActiveMedia(activeMedia);

        if (!this.elements.thumbnails) {
          this.announceLiveRegion(activeMedia, this.getActiveMediaIndex() + 1);
          return;
        }
        const activeThumbnail = this.elements.thumbnails.querySelector(`[data-target="${mediaId}"]`);
        this.setActiveThumbnail(activeThumbnail);
        this.announceLiveRegion(activeMedia, activeThumbnail?.dataset.mediaPosition || this.getActiveMediaIndex() + 1);
      }

      setActiveThumbnail(thumbnail) {
        if (!this.elements.thumbnails || !thumbnail) return;

        this.elements.thumbnails
          .querySelectorAll('button')
          .forEach((element) => element.removeAttribute('aria-current'));
        thumbnail.querySelector('button').setAttribute('aria-current', true);
        if (this.elements.thumbnails.isSlideVisible(thumbnail, 10)) return;

        this.elements.thumbnails.slider.scrollTo({ left: thumbnail.offsetLeft });
      }

      announceLiveRegion(activeItem, position) {
        const image = activeItem.querySelector('.product__modal-opener--image img');
        if (!image) return;
        image.onload = () => {
          this.elements.liveRegion.setAttribute('aria-hidden', false);
          this.elements.liveRegion.innerHTML = window.accessibilityStrings.imageAvailable.replace('[index]', position);
          setTimeout(() => {
            this.elements.liveRegion.setAttribute('aria-hidden', true);
          }, 2000);
        };
        image.src = image.src;
      }

      playActiveMedia(activeItem) {
        window.pauseAllMedia();
        const deferredMedia = activeItem.querySelector('.deferred-media');
        if (deferredMedia) deferredMedia.loadContent(false);
      }

      preventStickyHeader() {
        this.stickyHeader = this.stickyHeader || document.querySelector('sticky-header');
        if (!this.stickyHeader) return;
        this.stickyHeader.dispatchEvent(new Event('preventHeaderReveal'));
      }

      removeListSemantic() {
        if (!this.elements.viewer.slider) return;
        this.elements.viewer.slider.setAttribute('role', 'presentation');
        this.elements.viewer.sliderItems.forEach((slide) => slide.setAttribute('role', 'presentation'));
      }
    }
  );
}
